import { getCourses } from 'core/crud/courses.crud';
import { getGradebook } from 'core/crud/gradebook.crud';
import { useRouter } from 'core/hooks/useRouter';
import useSystemSettings from 'core/hooks/useSystemSettings';
import * as coreEntities from 'core/store/index';
import { handleApiError } from 'core/utils/apiErrorHandler';
import { COURSE_TYPES } from 'core/utils/constants/course';
import { getCustomizedBy, isCourseReadOnly } from 'core/utils/course';
import { validateAndFixJSON } from 'core/utils/jsonValidator';
import { successResponse } from 'core/utils/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { useUser } from './useUser';

export function useCourse(guid, updateCourse = false) {
  const dispatch = useDispatch();
  const { loadingSettings, enableEvaluationModel } = useSystemSettings();
  const { user, isStudent, schoolGuid } = useUser();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [themeColor, setThemeColor] = useState(getComputedStyle(document.documentElement).getPropertyValue('--color-first'));
  const router = useRouter();
  const { addToast } = useToasts();
  const { t } = useTranslation();

  const courseMode = useSelector((state) => coreEntities.ui.selectors.getCourseMode(state));
  const currentCourse = useSelector((state) => coreEntities.courses.selectors.getCurrentCourse(state));
  const isSchoolProgram = useSelector((state) => coreEntities.courses.selectors.isSchoolProgram(state, guid));

  useEffect(() => {
    if (enableEvaluationModel === undefined || loadingSettings) {
      return;
    }

    if (currentCourse && currentCourse.guid === guid && !updateCourse && isStudent) {
      setLoading(true);
      setData(currentCourse);
      try {
        const json = JSON.parse(currentCourse.template.json_config);
        if (json.color) setThemeColor(json.color);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    } else {
      getCurrentCourse();
    }
  }, [guid, enableEvaluationModel, isStudent]);

  async function initStudentGradebook(course, user) {
    const response = await getGradebook(course?.guid, getCustomizedBy(course, user), schoolGuid);
    if (successResponse(response)) {
      return response?.data?.data;
    }
  }

  async function getCurrentCourse() {
    if (!guid || loading === true) {
      return;
    } else {
      setLoading(true);
      setError(null);
      try {
        const response = await getCourses({ courseGuid: guid, schoolGuid: isSchoolProgram ? schoolGuid : null });

        if (successResponse(response)) {
          let tempCurrentCourse = {
            ...response.data.data,
            isUnattended: Boolean(response.data.data.type === COURSE_TYPES.TEMPLATE_1 || response.data.data.type === COURSE_TYPES.TEMPLATE_2),
            isCoursePersonalizedByTeacher: Boolean(
              response.data.data.type === COURSE_TYPES.TEMPLATE_2 || response.data.data.course_template_type === COURSE_TYPES.TEMPLATE_2
            ),
            hasHome: Boolean(response.data.data.has_home),
            isReadOnly: isCourseReadOnly(response.data.data),
          };

          if (enableEvaluationModel) {
            const studentGradebook = await initStudentGradebook(tempCurrentCourse, user);
            tempCurrentCourse.studentGradebook = studentGradebook;
          }

          setData(tempCurrentCourse);
          dispatch(coreEntities.courses.actions.setCurrentCourse(tempCurrentCourse));

          try {
            const json = validateAndFixJSON(tempCurrentCourse.template.json_config);
            if (json.color) setThemeColor(json.color);
          } catch (err) {
            console.error(err);
          }
          setLoading(false);
        } else {
          const { action, errorState } = handleApiError({
            context: 'useCourse',
            response,
            extra: { courseGuid: guid },
          });
          if (action === 'redirect-not-found') {
            router.history.push('/not-found');
          } else if (action === 'redirect-login') {
            router.history.push('/logout');
          } else if (errorState.status === 403) {
            addToast(t('courses:No active license'), { appearance: 'error', autoDismiss: true });
            router.history.push('/home');
          } else {
            setError(errorState);
          }
          setLoading(false);
        }
      } catch (err) {
        const { errorState } = handleApiError({
          context: 'useCourse.catch',
          response: { error: undefined },
          extra: { courseGuid: guid, errorMessage: err?.message },
        });
        setError(errorState);
        setLoading(false);
      }
    }
  }

  return { data, error, loading, courseMode, themeColor };
}
